import type { EntityId } from '../shared'

export enum PriorAuthorizationStatus {
  ManualStart = 'ManualStart',
  Pending = 'Pending',
  ProviderNotified = 'ProviderNotified',
  PayerNotified = 'PayerNotified',
  Approved = 'Approved',
  Denied = 'Denied',
  Error = 'Error',
  Cancelled = 'Cancelled',
  Inactive = 'Inactive',
  Complete = 'Complete',
}

export enum PriorAuthorizationStatusReason {
  Duplicate = 'Duplicate',
  Test = 'Test',
  CancelledByPrescriber = 'Cancelled by prescriber',
  CancelledByPayer = 'Cancelled by payer',
  NoResponseFromPrescriber = 'No Response from prescriber',
  NoResponseFromPayer = 'No Response from payer',
  InvalidIcd10 = 'Missing / Invalid ICD-10',
  NewPaRequestSubmitted = 'New PA request submitted',
  NeverSubmittedToPlan = 'Archived/Never submitted to plan',
  AnotherPaAlreadyOnFile = 'Another PA already on file',
  PaApprovedUnderDifferentKey = 'PA approved under different Key',
}

export enum PriorAuthorizationCallbackStatus {
  New = 'new',
  PrescriberNotified = 'prescriber notified',
  PayerNotified = 'payer notified',
  Pending = 'pending',
  Approved = 'approved',
  Denied = 'denied',
  Canceled = 'canceled',
}

export enum PriorAuthorizationOrigin {
  User = 'User',
  Copay = 'Copay',
}

export const ACTIONABLE_PRIOR_AUTHORIZATION_STATUS: string[] = [
  PriorAuthorizationStatus.Pending,
  PriorAuthorizationStatus.ManualStart,
  PriorAuthorizationStatus.ProviderNotified,
  PriorAuthorizationStatus.PayerNotified,
]

export const INACTIVE_PRIOR_AUTHORIZATION_STATUS = [
  PriorAuthorizationStatus.Cancelled,
  PriorAuthorizationStatus.Inactive,
  PriorAuthorizationStatus.Approved,
  PriorAuthorizationStatus.Denied,
]

export interface PriorAuthorizationActionButton {
  buttonLabel: string
  description: string
  url: string
  isPrimaryAction: boolean
  isRequiredAction: boolean
}

export interface PostClaimStatusServiceRequest {
  claimId: EntityId
  receivedEdi: string
  sentEdi: string
}

export type PriorAuthorizationServiceRequest = PriorAuthorizationServiceRequestV1 | PriorAuthorizationServiceRequestV2

export interface PriorAuthorizationServiceRequestV1 {
  prescriptionId: string
  claimId: string
  fillId: string
  userId: string
  locationId: string
  customerId: string
  patientId: string
  orderId?: string
  copayRequestId?: string
  prescriberId: string
  claimEdi: string
  rejectCode: string
  rejectMessage: string
  prescriberFax?: string
  medicationName: string
  force?: boolean
  origin?: string
  patientZip?: string
}

export interface PriorAuthorizationServiceRequestV2 {
  claim?: {
    id: string
    edi: string
    rejectCodes?: string[]
    additionalMessageInformation?: string
  }
  force?: boolean
  initiatedBy: {
    origin?: string
    userId: string
    orderId?: string
    copayRequestId?: string
  }
  insurance: {
    bin: string
    pcn?: string
    group?: string
    cardholderId: string
    personCode?: string
  }
  patient: {
    id: string
    firstName?: string
    lastName?: string
    dob?: string // ISO 8601 format (YYYY-MM-DD)
    gender?: 'male' | 'female'
    address?: {
      street1: string
      street2?: string
      city: string
      state: string
      zip: string
    }
  }
  pharmacy: {
    npi: string
    locationId: string
  }
  prescriber: {
    id: string
    npi?: string
    firstName?: string
    lastName?: string
    address?: {
      street1: string
      street2?: string
      city: string
      state: string
      zip: string
    }
    fax?: string
    phone?: string
  }
  prescription: {
    id: string
    customerId: string
    fillId?: string
    fillNumber?: number
    locationId: string
    medicationName: string
    rxNumber: number
    ndc?: string
    originCode?: number
    quantity?: number
    daysSupply?: number
  }
}

export interface PriorAuthorizationServiceResponse {
  status: string
  priorAuthorizationId?: string
  error?: string
}

export interface PriorAuthorizationServicePutRequest {
  rxNumber: number
  npi: string
  cmmKey: string
}

export interface PriorAuthorizationServicePutStatusRequest {
  message: string
  status: string
  cmmStatus?: string
  startedAt?: Date
  submittedAt?: Date
}

export interface PriorAuthorizationClaim {
  _id: EntityId
  createdAt: Date
  claimId: EntityId
  receivedEdi: string
  sentEdi: string
  sendToCMMDate?: Date
  updatedAt: Date
}

export interface PriorAuthorizationInput {
  actions: PriorAuthorizationActionButton[]
  claims: PriorAuthorizationClaim[]
  cmmKey?: string
  cmmStatus?: string
  customerId: EntityId
  initiatedBy: {
    claimId: EntityId
    copayRequestId?: EntityId
    fillId?: EntityId
    orderId?: EntityId
    userId: EntityId
    origin?: PriorAuthorizationOrigin
  }
  insurance?: {
    bin: string
    pcn?: string
    group?: string
    cardholderId: string
  }
  isManual?: boolean
  locationId: EntityId
  medicationName: string
  message?: string
  ndc: string
  npi: string
  patientId: EntityId
  prescriberFax: string
  prescriberId: EntityId
  prescriptionId: EntityId
  rxNumber: number
  status: PriorAuthorizationStatus
  urgent?: boolean
  submittedAt?: Date
  startedAt?: Date
  lastAttemptAt?: Date
  expiresOn?: Date
}

export const PRIOR_AUTHORIZATION_REJECT_CODES = [
  '70',
  '75',
  '76',
  'MR',
  'AG',
  'A5',
  'A6',
  '7X',
  '7Y',
  '9G',
  '608',
  '60',
  '80',
  '66',
  '64',
  'AJ',
  '61',
  '828',
]
